import { useSignIn } from '@clerk/clerk-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, Lock, Mail } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Heading,
  Input,
  InputGroup,
  Text,
} from '@/shared/ui';
import { currentInvitationTicket } from '../lib/invitation-ticket';
import {
  clerkErrorFields,
  reportInviteTrace,
} from '../lib/report-invite-trace';
import css from './sign-in-form.module.scss';

type FieldErrors = {
  email?: string;
  password?: string;
};

export const SignInForm = () => {
  const { signIn, setActive } = useSignIn();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState('');
  // Failed attempts in this mount, so a success can say whether it followed a
  // struggle. A ref, not state: it must survive re-renders without causing one.
  const failedAttempts = useRef(0);

  // An application invitation lands here because the Clerk instance's
  // redirect points at the sign-in page — but an invitee has no account yet,
  // so signing in is impossible and a fresh registration fails with "email
  // already exists" (Clerk reserved the address when the invitation was
  // created).
  // Hand the ticket to the sign-up form, which redeems it via the ticket
  // strategy. Runs before the transfer effect below so we never spend the
  // invitation on a `transfer: true` attempt that cannot succeed.
  useEffect(() => {
    const ticket = currentInvitationTicket();
    if (!ticket) return;
    // First proof the invitee ever clicked the link. Everything before this is
    // Clerk's to report; everything after is ours.
    reportInviteTrace('link.opened', {
      ticket,
      // `landed` is the diagnosis: reaching sign-in first means Clerk's
      // redirect still points at the wrong page. Where we send them next is
      // always sign-up, so it needs no field.
      fields: { landed: 'sign-in' },
    });
    navigate({
      to: '/sign-up',
      search: { __clerk_ticket: ticket },
      replace: true,
    });
  }, [navigate]);

  // Attempt cross-domain session transfer from the primary Clerk domain.
  //
  // Only fire when the URL carries a Clerk transfer marker
  // (`__clerk_ticket` / `__clerk_handshake` / `__clerk_db_jwt`), which
  // means the user actually arrived from the primary. Without this
  // gate, every mount on a standalone satellite (e.g. the agent
  // sandbox) fires a doomed `signIn.create({ transfer: true })` that
  // 400s and quickly trips Clerk's per-IP rate limit (429) — taking
  // the whole SDK down with a generic "Something went wrong".
  //
  // Also guarded by a per-tab sessionStorage flag so a refresh on the
  // sign-in page doesn't replay the attempt.
  useEffect(() => {
    if (import.meta.env.DEV) return;
    if (!signIn || !setActive) return;
    if (typeof window === 'undefined') return;

    // An invitation ticket is not a transferable session — it is redeemed on
    // the sign-up page by the effect above.
    if (currentInvitationTicket()) return;

    const params = new URLSearchParams(window.location.search);
    const hasTransferMarker =
      params.has('__clerk_ticket') ||
      params.has('__clerk_handshake') ||
      params.has('__clerk_db_jwt');
    if (!hasTransferMarker) return;

    const TRIED_KEY = 'clerk-transfer-attempted';
    if (window.sessionStorage.getItem(TRIED_KEY)) return;
    window.sessionStorage.setItem(TRIED_KEY, '1');

    signIn
      .create({ transfer: true })
      .then((result) => {
        if (result.status === 'complete' && result.createdSessionId) {
          setActive({ session: result.createdSessionId });
        }
      })
      .catch((err: unknown) => {
        // No transferable session — the user must sign in manually. Previously
        // swallowed without a word, so a broken cross-domain handshake looked
        // exactly like a user who simply never typed a password.
        reportInviteTrace('signin.transfer_failed', {
          fields: clerkErrorFields(err),
        });
      });
  }, [signIn, setActive]);

  const validate = useCallback(() => {
    const errors: FieldErrors = {};
    if (!email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email))
      errors.email = 'Please enter a valid email address';
    if (!password) errors.password = 'Password is required';
    return errors;
  }, [email, password]);

  const handleSignIn = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      setError('');
      const errors = validate();
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      try {
        const result = await signIn?.create({
          identifier: email,
          password,
        });
        if (result?.status === 'complete' && result.createdSessionId) {
          // The answer to "did they eventually get in?". `afterFailures` is the
          // point of this step: a success with 0 is routine, a success with 2
          // means someone fought their way past a wrong password — and the
          // absence of this line after a run of failures means they never did.
          reportInviteTrace('signin.ok', {
            email,
            fields: { afterFailures: failedAttempts.current || undefined },
          });
          await setActive?.({ session: result.createdSessionId });
        } else if (result?.status) {
          // Clerk wants another factor (or something else entirely) and this
          // form has no step for it: no error renders and nothing navigates,
          // so the page just looks dead. Report what Clerk actually said.
          //
          // Only when Clerk RETURNED a status. An absent result means the SDK
          // had nothing to answer with (it was still loading, or the session
          // already existed), which happens during ordinary successful logins —
          // reporting those flooded the channel with false stalls.
          reportInviteTrace('signin.stalled', {
            email,
            fields: { status: result.status },
          });
        }
      } catch (err) {
        failedAttempts.current += 1;
        reportInviteTrace('signin.failed', {
          email,
          fields: { attempt: failedAttempts.current, ...clerkErrorFields(err) },
        });
        setError(
          err instanceof Error ? err.message : 'Invalid email or password'
        );
      }
    },
    [signIn, setActive, email, password, validate]
  );

  return (
    <form className={css.root} onSubmit={handleSignIn}>
      <div className={css.header}>
        <Heading as="h1" size="2xl">
          Hello!
        </Heading>
        <Text as="p" size="lg">
          Sign in to see your profile.
        </Text>
        <Text as="p" variant="muted">
          Enter your credentials below.
        </Text>
      </div>

      <div className={css.fields}>
        <InputGroup
          error={fieldErrors.email}
          htmlFor="sign-in-email"
          label="Email"
        >
          <Input
            autoComplete="email"
            id="sign-in-email"
            name="email"
            placeholder="youremail@gmail.com"
            trailingIcon={<Mail size={16} />}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </InputGroup>

        <InputGroup
          error={fieldErrors.password}
          htmlFor="sign-in-password"
          label="Password"
        >
          <Input
            autoComplete="current-password"
            id="sign-in-password"
            name="password"
            placeholder="Start typing..."
            trailingIcon={<Lock size={16} />}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </InputGroup>
      </div>

      <div className={css.options}>
        <Checkbox
          checked={remember}
          label="Remember me"
          onChange={(e) => setRemember(e.target.checked)}
        />
        <Link className={css.recover} to="/forgot-password">
          Reset Password
        </Link>
      </div>

      <div className={css.actions}>
        <Button size="xl" type="submit" variant="dark">
          Login
        </Button>
      </div>

      {error && <Alert icon={<AlertCircle size={16} />}>{error}</Alert>}
    </form>
  );
};
